import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, MonitorPlay, RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Bar, STATUS } from "@/pages/Rmm";

const Card = ({ title, children, action }) => (
  <div className="rounded-lg border border-slate-200 bg-white">
    <div className="flex items-center px-4 py-2.5 border-b border-slate-100"><p className="font-medium text-sm mr-auto">{title}</p>{action}</div>
    <div className="p-4">{children}</div>
  </div>
);
const Row = ({ k, v }) => <div className="flex justify-between gap-4 py-1 text-sm"><span className="text-slate-500">{k}</span><span className="text-right font-medium">{v ?? "—"}</span></div>;
const onOff = (o) => (o == null ? "Inconnu" : o.enabled ? "Actif" : "Désactivé");

export const Health = ({ d }) => {
  const h = d.health || {};
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card title="Performances">
        <Row k="Processeur" v={<Bar v={h.cpu} />} />
        <Row k="Mémoire" v={<Bar v={h.ram} />} />
        <Row k="Dernier démarrage" v={h.boot_at ? fmtDate(new Date(h.boot_at * 1000).toISOString()) : null} />
        <Row k="Redémarrage requis" v={h.reboot_pending ? "Oui" : "Non"} />
      </Card>
      <Card title="Disques">
        {(h.disks || []).map((x) => <div key={x.mount} className="py-1"><Row k={`${x.mount} — ${x.free_gb} Go libres / ${x.total_gb} Go`} v="" /><Bar v={x.percent} /></div>)}
        {!h.disks?.length && <p className="text-sm text-slate-500">En attente du premier rapport…</p>}
      </Card>
      <Card title="Sécurité">
        <Row k="Antivirus" v={h.av ? `${h.av.name} · ${onOff(h.av)}` : "Inconnu"} />
        <Row k="Définitions antivirus" v={h.av ? (h.av.up_to_date ? "À jour" : "Pas à jour") : null} />
        <Row k="Pare-feu" v={onOff(h.firewall)} />
        <Row k="Mises à jour système en attente" v={d.os_updates} />
      </Card>
    </div>
  );
};

const UpdateList = ({ items, sel, setSel, render, testid }) => (
  <div className="divide-y" data-testid={testid}>
    {items.map((u) => (
      <label key={u.id} className="flex items-start gap-3 py-2.5 cursor-pointer" data-testid={`${testid}-${u.id}`}>
        <Checkbox checked={sel.includes(u.id)} onCheckedChange={(c) => setSel((s) => (c ? [...s, u.id] : s.filter((x) => x !== u.id)))} className="mt-0.5" />
        <div className="flex-1 min-w-0 text-sm">{render(u)}</div>
      </label>
    ))}
  </div>
);

export const Updates = ({ d, id, reload }) => {
  const [osSel, setOsSel] = useState([]);
  const [appSel, setAppSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const upd = d.updates || {};
  const apps = upd.apps || {};
  const scan = () => api.post(`/rmm/machines/${id}/scan`).then(() => { toast.success("Recherche en cours sur le poste…"); reload(); }).catch((e) => toast.error(errMsg(e)));
  const install = async () => {
    setBusy(true);
    try {
      await api.post(`/rmm/machines/${id}/install`, { os_ids: osSel, app_ids: appSel });
      toast.success("Installation lancée. L'utilisateur sera invité à redémarrer si nécessaire.");
      setOsSel([]); setAppSel([]); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const running = (d.jobs || []).find((j) => ["queued", "running"].includes(j.status));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-slate-500 mr-auto" data-testid="rmm-updates-at">{upd.at ? `Dernière analyse : ${fmtDate(upd.at)}` : "Pas encore analysé."}{d.scanning && " · analyse en cours…"}</p>
        <Button variant="outline" onClick={scan} disabled={!d.online || d.scanning} data-testid="rmm-scan-btn">{d.scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Rechercher</Button>
        <Button onClick={install} disabled={busy || !d.online || !(osSel.length + appSel.length)} data-testid="rmm-install-btn"><Download className="h-4 w-4 mr-2" /> Installer la sélection ({osSel.length + appSel.length})</Button>
      </div>
      {running && <p className="rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm px-3 py-2" data-testid="rmm-job-running"><Loader2 className="h-4 w-4 inline mr-2 animate-spin" />{running.step || "Installation en attente…"}</p>}
      {d.error && <p className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{d.error}</p>}
      <div className="grid xl:grid-cols-2 gap-4">
        <Card title={`Système (${(upd.os || []).length})`} action={(upd.os || []).length > 0 && <Button size="sm" variant="ghost" onClick={() => setOsSel(upd.os.map((u) => u.id))} data-testid="rmm-select-all-os">Tout sélectionner</Button>}>
          {(upd.os || []).length ? <UpdateList items={upd.os} sel={osSel} setSel={setOsSel} testid="rmm-os-update" render={(u) => <>
            <p className="font-medium">{u.title}</p>
            <p className="text-xs text-slate-500">{[u.category, u.size_mb ? `${u.size_mb} Mo` : null, u.reboot && "redémarrage requis"].filter(Boolean).join(" · ")}
              {["Critical", "Critique"].includes(u.severity) && <span className="ml-2 rounded bg-red-100 text-red-700 px-1.5 py-0.5 text-[10px] font-semibold">CRITIQUE</span>}</p>
          </>} /> : <p className="text-sm text-slate-500">{upd.at ? "Système à jour." : "Lancez une recherche."}</p>}
        </Card>
        <Card title={`Logiciels${apps.tool ? ` via ${apps.tool}` : ""} (${(apps.items || []).length})`} action={(apps.items || []).length > 0 && <Button size="sm" variant="ghost" onClick={() => setAppSel(apps.items.map((u) => u.id))} data-testid="rmm-select-all-apps">Tout sélectionner</Button>}>
          {apps.available === false && <p className="text-sm text-amber-700">{apps.tool} n'est pas disponible sur ce poste : la mise à jour des logiciels n'est pas possible.</p>}
          {(apps.items || []).length ? <UpdateList items={apps.items} sel={appSel} setSel={setAppSel} testid="rmm-app-update" render={(u) => <>
            <p className="font-medium">{u.title}</p><p className="text-xs text-slate-500">{u.version} → <span className="text-emerald-700 font-medium">{u.available}</span></p>
          </>} /> : apps.available !== false && <p className="text-sm text-slate-500">{upd.at ? "Logiciels à jour." : "Lancez une recherche."}</p>}
        </Card>
      </div>
    </div>
  );
};

export const Inventory = ({ d, id }) => {
  const [q, setQ] = useState("");
  const inv = d.inventory || {};
  const hw = inv.hardware || {};
  const sw = (inv.software || []).filter((s) => `${s.name} ${s.publisher}`.toLowerCase().includes(q.toLowerCase()));
  const refresh = () => api.post(`/rmm/machines/${id}/inventory`).then(() => toast.success("Inventaire demandé au poste")).catch((e) => toast.error(errMsg(e)));
  const [rm, setRm] = useState([]);
  const canRemove = (inv.software || []).some((s) => s.uninstall);
  const toggleRm = (n) => setRm((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));
  const uninstall = () => {
    if (!window.confirm(`Désinstaller ${rm.length} logiciel(s) de ce poste ?\n\n${rm.join("\n")}`)) return;
    api.post(`/rmm/machines/${id}/uninstall`, { names: rm })
      .then(() => { toast.success("Désinstallation lancée : suivez-la dans l'onglet Historique"); setRm([]); })
      .catch((e) => toast.error(errMsg(e)));
  };
  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-4 items-start">
      <Card title="Matériel" action={<Button size="sm" variant="ghost" onClick={refresh} disabled={!d.online} data-testid="rmm-inventory-refresh"><RefreshCw className="h-4 w-4" /></Button>}>
        {[["Fabricant", hw.maker], ["Modèle", hw.model], ["N° de série", hw.serial], ["Processeur", hw.cpu], ["Cœurs", hw.cpu_cores], ["Mémoire", hw.ram_gb && `${hw.ram_gb} Go`],
          ["Système", hw.os], ["Version", hw.os_version], ["BIOS", hw.bios], ["Domaine", hw.domain], ["IP locale", d.ip_local], ["Utilisateur", d.username]].map(([k, v]) => <Row key={k} k={k} v={v} />)}
        <p className="text-[11px] text-slate-400 mt-2">{inv.at ? `Relevé le ${fmtDate(new Date(inv.at * 1000).toISOString())}` : "En attente du premier inventaire"}</p>
      </Card>
      <Card title={`Logiciels installés (${(inv.software || []).length})`}>
        <div className="flex items-center gap-3 mb-3">
          <Input placeholder="Rechercher un logiciel" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="rmm-software-search" />
          {canRemove && <Button size="sm" variant="destructive" disabled={!rm.length || !d.online} onClick={uninstall} data-testid="rmm-uninstall-btn">Désinstaller{rm.length ? ` (${rm.length})` : ""}</Button>}
          {canRemove && !d.online && <span className="text-xs text-slate-500">Poste hors ligne : la désinstallation sera possible quand il sera allumé.</span>}
          {!canRemove && (inv.software || []).length > 0 && <span className="text-xs text-slate-500">Mettez l'agent à jour (1.6.6) pour pouvoir désinstaller des logiciels.</span>}
        </div>
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-sm" data-testid="rmm-software-table">
            <thead className="text-left text-xs text-slate-500 sticky top-0 bg-white"><tr>{canRemove && <th className="w-7" />}<th className="py-1.5">Nom</th><th>Version</th><th>Éditeur</th></tr></thead>
            <tbody>{sw.map((s) => <tr key={s.name} className="border-t border-slate-100">{canRemove && <td>{s.uninstall && <input type="checkbox" checked={rm.includes(s.name)} onChange={() => toggleRm(s.name)} className="accent-red-600" data-testid={`rmm-uninstall-check-${s.name}`} />}</td>}<td className="py-1.5 pr-3">{s.name}</td><td className="pr-3 tabular-nums text-slate-600">{s.version}</td><td className="text-slate-500">{s.publisher}</td></tr>)}</tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const JOB = { queued: "En attente", running: "En cours", done: "Terminé" };
const History = ({ d }) => (
  <div className="grid lg:grid-cols-2 gap-4">
    <Card title="Installations et désinstallations">
      {!(d.jobs || []).length && <p className="text-sm text-slate-500">Aucune installation.</p>}
      {(d.jobs || []).map((j) => (
        <div key={j.job_id} className="py-2 border-b last:border-0 border-slate-100 text-sm" data-testid={`rmm-job-${j.job_id}`}>
          <p className="font-medium">{JOB[j.status] || j.status} · {fmtDate(j.created_at)} · par {j.by}</p>
          {(j.results || []).map((r) => <p key={r.id} className={`text-xs ${r.ok ? "text-emerald-700" : "text-red-600"}`}>{r.ok ? "✓" : "✗"} {j.titles?.[r.id] || r.id} — {r.message}</p>)}
          {j.reboot && <p className="text-xs text-amber-700 flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Redémarrage {j.reboot_answer === "accepted" ? "accepté par l'utilisateur" : j.reboot_answer === "scheduled" ? `programmé par l'utilisateur le ${fmtDate(j.reboot_at)}` : j.reboot_answer === "later" ? "reporté par l'utilisateur (il redémarrera lui-même)" : "proposé à l'utilisateur"}</p>}
        </div>
      ))}
    </Card>
    <Card title="Alertes">
      {!(d.alerts || []).length && <p className="text-sm text-slate-500">Aucune alerte.</p>}
      {(d.alerts || []).map((a) => <p key={a.alert_id} className="text-sm py-1"><span className={a.status === "open" ? "text-red-600 font-medium" : "text-slate-400 line-through"}>{a.label}</span> <span className="text-xs text-slate-500">— {a.message} · {fmtDate(a.at)}</span></p>)}
    </Card>
  </div>
);

export default function RmmMachine() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/rmm/machines/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  if (!d) return <div className="flex-1 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const [label, dot, txt] = STATUS[d.status];
  return (
    <div className="flex-1 min-w-0 overflow-auto bg-slate-50 p-6 lg:p-8 space-y-5" data-testid="rmm-machine-page">
      <Link to="/app/rmm" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="rmm-back"><ArrowLeft className="h-4 w-4" /> Tous les postes</Link>
      <div className="flex flex-wrap items-center gap-4">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold" data-testid="rmm-machine-name">{d.name}</h1>
          <p className="text-sm text-slate-500 flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 font-medium ${txt}`} data-testid="rmm-machine-status"><span className={`h-2 w-2 rounded-full ${dot}`} />{label}</span>
            <span>{d.os_version || d.os}</span><span>{d.online ? "En ligne" : `Hors ligne depuis ${fmtDate(d.last_seen)}`}</span>
          </p>
        </div>
        <Button asChild variant="outline" disabled={!d.online}><a href={`/session/${id}`} target="_blank" rel="noreferrer" data-testid="rmm-open-session"><MonitorPlay className="h-4 w-4 mr-2" /> Prendre la main</a></Button>
      </div>
      <Tabs defaultValue="health">
        <TabsList>
          <TabsTrigger value="health" data-testid="rmm-tab-health">Santé</TabsTrigger>
          <TabsTrigger value="updates" data-testid="rmm-tab-updates">Mises à jour{d.os_updates + d.app_updates ? ` (${d.os_updates + d.app_updates})` : ""}</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="rmm-tab-inventory">Inventaire</TabsTrigger>
          <TabsTrigger value="history" data-testid="rmm-tab-history">Historique</TabsTrigger>
        </TabsList>
        <TabsContent value="health"><Health d={d} /></TabsContent>
        <TabsContent value="updates"><Updates d={d} id={id} reload={load} /></TabsContent>
        <TabsContent value="inventory"><Inventory d={d} id={id} /></TabsContent>
        <TabsContent value="history"><History d={d} /></TabsContent>
      </Tabs>
    </div>
  );
}
